import type { Disposable } from "@/shared/contracts/lifecycle";
import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import { compareHuge, type HugeNumber } from "@/shared/numbers";

export type OfflineRewardResultModalDismissReason =
  | "dismiss-button"
  | "dismiss-hover"
  | "dismiss-close"
  | "dismiss-backdrop";

export interface OfflineRewardResultModalMessages {
  readonly title: string;
  readonly elapsedLabel: (elapsed: string) => string;
  readonly appliedNotice: string;
  readonly timGainLabel: (amount: string) => string;
  readonly hoverAnchorNotice: string;
  readonly close: string;
  readonly hoverClose: string;
  readonly closeAriaLabel: string;
}

export interface OfflineRewardResultModalModel {
  readonly rewardId: string;
  readonly appliedGameElapsedMs: number;
  readonly wasCapped: boolean;
  readonly rewards: {
    readonly tim: HugeNumber;
  };
}

export interface OfflineRewardResultModal extends Disposable {}

export function createOfflineRewardResultModal(options: {
  readonly isTouchPrimary: boolean;
  readonly showHoverAnchorNotice?: boolean;
  readonly model: OfflineRewardResultModalModel;
  readonly messages: OfflineRewardResultModalMessages;
  readonly formatDuration: (ms: number) => string;
  readonly onDismiss: (reason: OfflineRewardResultModalDismissReason) => void;
  readonly parent?: HTMLElement;
}): OfflineRewardResultModal {
  const parent = options.parent ?? document.body;
  const { messages, model } = options;

  const backdrop = document.createElement("div");
  backdrop.className = "hover-idle-offline-modal-backdrop";
  backdrop.dataset.offlineRewardResult = "true";
  backdrop.dataset.rewardId = model.rewardId;
  backdrop.setAttribute("data-no-virtual-pointer", "");

  const dialog = document.createElement("div");
  dialog.className = "hover-idle-offline-modal";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-labelledby", "hover-idle-offline-modal-title");
  dialog.tabIndex = -1;

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = "hover-idle-offline-modal__close";
  closeButton.setAttribute("aria-label", messages.closeAriaLabel);
  closeButton.textContent = "×";

  const title = document.createElement("h2");
  title.className = "hover-idle-offline-modal__title";
  title.id = "hover-idle-offline-modal-title";
  title.textContent = messages.title;

  const elapsed = document.createElement("p");
  elapsed.className = "hover-idle-offline-modal__elapsed";
  elapsed.textContent = messages.elapsedLabel(
    options.formatDuration(model.appliedGameElapsedMs),
  );

  const body = document.createElement("div");
  body.className = "hover-idle-offline-modal__body";

  const rewardList = document.createElement("div");
  rewardList.className = "hover-idle-offline-modal__rewards";
  const rewardLines = createRewardLines(model, messages);
  rewardList.append(...rewardLines);

  const appliedNotice = document.createElement("p");
  appliedNotice.className = "hover-idle-offline-modal__notice";
  appliedNotice.textContent = messages.appliedNotice;
  appliedNotice.hidden = !model.wasCapped;

  const hoverAnchorNotice = document.createElement("p");
  hoverAnchorNotice.className = "hover-idle-offline-modal__notice";
  hoverAnchorNotice.textContent = messages.hoverAnchorNotice;
  hoverAnchorNotice.hidden =
    !options.isTouchPrimary || options.showHoverAnchorNotice === false;

  body.append(rewardList, appliedNotice, hoverAnchorNotice);

  const actions = document.createElement("div");
  actions.className = "hover-idle-offline-modal__actions";

  const buttonGroup = document.createElement("div");
  buttonGroup.className = "hover-idle-offline-modal__button-group";

  const close = document.createElement("button");
  close.type = "button";
  close.className =
    "hover-idle-offline-modal__button hover-idle-offline-modal__button--confirm";
  close.textContent = messages.close;

  const hoverClose = document.createElement("button");
  hoverClose.type = "button";
  hoverClose.className =
    "hover-idle-offline-modal__button hover-idle-offline-modal__button--hover-confirm";
  hoverClose.textContent = messages.hoverClose;

  buttonGroup.append(close);
  if (!options.isTouchPrimary) buttonGroup.append(hoverClose);
  actions.append(buttonGroup);

  dialog.append(closeButton, title, elapsed, body, actions);
  backdrop.append(dialog);
  parent.append(backdrop);
  const restoreBackgroundInteractivity = inertBackgroundSiblings(
    parent,
    backdrop,
  );

  let isDismissed = false;
  const dismissBy = (reason: OfflineRewardResultModalDismissReason): void => {
    if (isDismissed) return;
    isDismissed = true;
    options.onDismiss(reason);
  };
  const onCloseClick = (): void => dismissBy("dismiss-button");
  const onRightCloseClick = (): void => dismissBy("dismiss-close");
  const onBackdropClick = (event: MouseEvent): void => {
    if (event.target === backdrop) dismissBy("dismiss-backdrop");
  };
  const onHoverClose = (): void => dismissBy("dismiss-hover");
  const onDialogClick = (event: MouseEvent): void => event.stopPropagation();

  close.addEventListener("click", onCloseClick);
  closeButton.addEventListener("click", onRightCloseClick);
  backdrop.addEventListener("click", onBackdropClick);
  hoverClose.addEventListener("pointerenter", onHoverClose);
  hoverClose.addEventListener("focus", onHoverClose);
  dialog.addEventListener("click", onDialogClick);

  queueMicrotask(() => {
    if (!isDismissed) close.focus({ preventScroll: true });
  });

  return {
    dispose(): void {
      isDismissed = true;
      close.removeEventListener("click", onCloseClick);
      closeButton.removeEventListener("click", onRightCloseClick);
      backdrop.removeEventListener("click", onBackdropClick);
      hoverClose.removeEventListener("pointerenter", onHoverClose);
      hoverClose.removeEventListener("focus", onHoverClose);
      dialog.removeEventListener("click", onDialogClick);
      restoreBackgroundInteractivity();
      backdrop.remove();
    },
  };
}

function createRewardLines(
  model: OfflineRewardResultModalModel,
  messages: OfflineRewardResultModalMessages,
): HTMLElement[] {
  const lines: HTMLElement[] = [];
  appendPositiveHugeRewardLine(lines, model.rewards.tim, messages.timGainLabel);
  return lines;
}

function appendPositiveHugeRewardLine(
  lines: HTMLElement[],
  amount: HugeNumber,
  label: (amount: string) => string,
): void {
  if (compareHuge(amount, 0) <= 0) return;
  const line = document.createElement("p");
  line.className = "hover-idle-offline-modal__line";
  line.textContent = label(formatForContext(amount, "main"));
  lines.push(line);
}

function inertBackgroundSiblings(
  parent: HTMLElement,
  modalRoot: HTMLElement,
): () => void {
  const inertedElements: Element[] = [];
  for (const child of Array.from(parent.children)) {
    if (child === modalRoot || child.hasAttribute("inert")) continue;
    child.setAttribute("inert", "");
    inertedElements.push(child);
  }
  return () => {
    for (const element of inertedElements) element.removeAttribute("inert");
  };
}
